import { G, clamp, wrapIndex } from '../core/math';
import { MASS, MAX_BRAKE_FORCE, MAX_TRACTION_FORCE, SURFACES } from '../physics/constants';
import type { Track } from '../track/generate';
import type { SpeedProfile } from '../track/speedprofile';
import type { Corner } from '../track/brakingzones';
import type { Ghost } from '../game/session';
import { GHOST_STRIDE } from '../game/session';
import { TICK_DT } from '../physics/constants';

const FONT = 'ui-monospace, SFMono-Regular, Menlo, monospace';

export interface TelemetryModel {
  ghost: Ghost;
  /** Cumulative time lost against the ideal lap at each frame, seconds. */
  delta: Float32Array;
  /** Distance travelled along the centerline at each frame, m. */
  distance: Float32Array;
  /** Ideal speed at each frame's position, for the overlay. */
  idealSpeed: Float32Array;
  /** Frame index where each corner's braking zone begins. */
  cornerFrames: Array<{ number: number; frame: number }>;
  sectorFrames: number[];
  totalDistance: number;
  worstFrame: number;
}

/**
 * Precomputes everything the telemetry charts need.
 *
 * The delta trace is the one that matters. A speed trace shows what happened;
 * a delta trace shows what it *cost*, and where. Comparing elapsed time at a
 * point on track against the time the ideal lap would have reached the same
 * point turns "you were slow through there" into a number of tenths.
 */
export function buildTelemetry(
  track: Track,
  profile: SpeedProfile,
  corners: Corner[],
  ghost: Ghost,
): TelemetryModel {
  const n = ghost.x.length;
  const ds = track.spacing;

  // Cumulative ideal time to reach each centerline index from the start line.
  const centerCount = track.center.length;
  const idealTime = new Float64Array(centerCount);
  let running = 0;
  for (let s = 0; s < centerCount; s++) {
    const i = wrapIndex(track.startIndex + s, centerCount);
    const j = wrapIndex(i + 1, centerCount);
    idealTime[i] = running;
    running += ds / Math.max(0.5, (profile.vIdeal[i] + profile.vIdeal[j]) / 2);
  }

  const delta = new Float32Array(n);
  const distance = new Float32Array(n);
  const idealSpeed = new Float32Array(n);
  let worstFrame = 0;

  // Position along the lap is modular, but distance and elapsed ideal time are
  // not. Crossing the line resets the index to zero, which without this would
  // drop the final frames back to the start of the lap — the delta trace would
  // finish reading the entire lap time as time lost.
  let lapOffset = 0;
  let prevAlong = -1;

  for (let f = 0; f < n; f++) {
    const idx = ghost.index[f] ?? 0;
    const along = wrapIndex(idx - track.startIndex, centerCount);
    if (prevAlong >= 0 && along < prevAlong - centerCount / 2) lapOffset++;
    prevAlong = along;

    distance[f] = (along + lapOffset * centerCount) * ds;
    idealSpeed[f] = profile.vIdeal[idx];

    const elapsed = f * GHOST_STRIDE * TICK_DT;
    delta[f] = elapsed - (idealTime[idx] + lapOffset * profile.idealLapTime);
    if (delta[f] > delta[worstFrame]) worstFrame = f;
  }

  // Where each corner's braking zone starts, mapped into frame space so the
  // charts can be annotated with the thing the game is actually about.
  const cornerFrames = corners
    .map((c) => {
      let best = -1;
      let bestDist = Infinity;
      for (let f = 0; f < n; f++) {
        const d = Math.abs(wrapIndex(ghost.index[f] - c.brakeIndex, centerCount));
        const wrapped = Math.min(d, centerCount - d);
        if (wrapped < bestDist) {
          bestDist = wrapped;
          best = f;
        }
      }
      return { number: c.number, frame: best };
    })
    .filter((c) => c.frame >= 0)
    .sort((a, b) => a.frame - b.frame);

  const sectorFrames = track.sectorStarts.slice(1).map((start) => {
    let best = 0;
    let bestDist = Infinity;
    for (let f = 0; f < n; f++) {
      const d = Math.abs(wrapIndex(ghost.index[f] - start, centerCount));
      const wrapped = Math.min(d, centerCount - d);
      if (wrapped < bestDist) {
        bestDist = wrapped;
        best = f;
      }
    }
    return best;
  });

  return {
    ghost,
    delta,
    distance,
    idealSpeed,
    cornerFrames,
    sectorFrames,
    totalDistance: track.length,
    worstFrame,
  };
}

interface ChartRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

function plotSeries(
  ctx: CanvasRenderingContext2D,
  rect: ChartRect,
  data: ArrayLike<number>,
  min: number,
  max: number,
  colour: string,
  width = 1.6,
  fill = false,
): void {
  if (data.length < 2) return;
  const scale = (v: number): number =>
    rect.y + rect.h - ((clamp(v, min, max) - min) / (max - min)) * rect.h;

  ctx.beginPath();
  for (let i = 0; i < data.length; i++) {
    const x = rect.x + (i / (data.length - 1)) * rect.w;
    const y = scale(data[i]);
    i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
  }
  if (fill) {
    ctx.lineTo(rect.x + rect.w, scale(clamp(0, min, max)));
    ctx.lineTo(rect.x, scale(clamp(0, min, max)));
    ctx.closePath();
    ctx.fillStyle = colour;
    ctx.fill();
  } else {
    ctx.strokeStyle = colour;
    ctx.lineWidth = width;
    ctx.stroke();
  }
}

function chartFrame(
  ctx: CanvasRenderingContext2D,
  rect: ChartRect,
  label: string,
  hi: string,
  lo: string,
): void {
  ctx.fillStyle = 'rgba(255,255,255,0.025)';
  ctx.fillRect(rect.x, rect.y, rect.w, rect.h);
  ctx.fillStyle = '#6f7b8c';
  ctx.font = `700 10px ${FONT}`;
  ctx.textAlign = 'left';
  ctx.fillText(label, rect.x + 6, rect.y + 12);
  ctx.textAlign = 'right';
  ctx.fillStyle = '#4d5663';
  ctx.fillText(hi, rect.x + rect.w - 6, rect.y + 12);
  ctx.fillText(lo, rect.x + rect.w - 6, rect.y + rect.h - 5);
  ctx.textAlign = 'left';
}

/** Vertical markers for sector lines and corner braking points. */
function annotate(
  ctx: CanvasRenderingContext2D,
  rect: ChartRect,
  model: TelemetryModel,
  frames: number,
  showCorners: boolean,
): void {
  for (const s of model.sectorFrames) {
    const x = rect.x + (s / Math.max(1, frames - 1)) * rect.w;
    ctx.strokeStyle = 'rgba(123,167,212,0.4)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x, rect.y);
    ctx.lineTo(x, rect.y + rect.h);
    ctx.stroke();
  }
  if (!showCorners) return;
  ctx.font = `700 9px ${FONT}`;
  ctx.textAlign = 'center';
  for (const c of model.cornerFrames) {
    const x = rect.x + (c.frame / Math.max(1, frames - 1)) * rect.w;
    ctx.strokeStyle = 'rgba(255,138,61,0.35)';
    ctx.setLineDash([2, 3]);
    ctx.beginPath();
    ctx.moveTo(x, rect.y);
    ctx.lineTo(x, rect.y + rect.h);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = 'rgba(255,138,61,0.75)';
    ctx.fillText(`T${c.number}`, x, rect.y + rect.h - 4);
  }
  ctx.textAlign = 'left';
}

export interface TelemetryReadout {
  speedKph: number;
  brake: number;
  steer: number;
  latG: number;
  longG: number;
  deltaS: number;
  distanceM: number;
  frontLoad: number;
  rearLoad: number;
}

export function readoutAt(model: TelemetryModel, frame: number): TelemetryReadout {
  const g = model.ghost;
  const f = clamp(Math.round(frame), 0, g.x.length - 1);
  return {
    speedKph: (g.speed[f] ?? 0) * 3.6,
    brake: g.brake[f] ?? 0,
    steer: g.steer[f] ?? 0,
    latG: g.latG[f] ?? 0,
    longG: g.longG[f] ?? 0,
    deltaS: model.delta[f] ?? 0,
    distanceM: model.distance[f] ?? 0,
    frontLoad: g.frontLoad[f] ?? 0,
    rearLoad: g.rearLoad[f] ?? 0,
  };
}

/**
 * Draws the stacked trace charts: speed against ideal, cumulative delta, inputs,
 * and axle grip usage. A playhead runs through all four so a point in one chart
 * is the same instant in the others.
 */
export function drawTraces(
  canvas: HTMLCanvasElement,
  model: TelemetryModel,
  frame: number,
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const cssW = canvas.clientWidth || canvas.width;
  const cssH = canvas.clientHeight || canvas.height;
  if (canvas.width !== cssW * dpr || canvas.height !== cssH * dpr) {
    canvas.width = cssW * dpr;
    canvas.height = cssH * dpr;
  }
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, cssW, cssH);

  const g = model.ghost;
  const frames = g.x.length;
  if (frames < 2) return;

  const gap = 6;
  const heights = [0.36, 0.24, 0.2, 0.2];
  const totalH = cssH - gap * (heights.length - 1);
  let y = 0;
  const rects: ChartRect[] = heights.map((share) => {
    const rect = { x: 0, y, w: cssW, h: totalH * share };
    y += rect.h + gap;
    return rect;
  });

  // --- speed against the ideal -------------------------------------------
  let vMax = 0;
  for (let i = 0; i < frames; i++) vMax = Math.max(vMax, g.speed[i], model.idealSpeed[i]);
  vMax = Math.max(vMax, 1);
  chartFrame(ctx, rects[0], 'SPEED', `${Math.round(vMax * 3.6)}`, '0 km/h');
  annotate(ctx, rects[0], model, frames, true);
  plotSeries(ctx, rects[0], model.idealSpeed, 0, vMax, 'rgba(255,255,255,0.3)', 1.4);
  plotSeries(ctx, rects[0], g.speed, 0, vMax, '#ff8a3d', 2);

  // --- cumulative delta ---------------------------------------------------
  let dMax = 0.5;
  for (let i = 0; i < frames; i++) dMax = Math.max(dMax, model.delta[i]);
  chartFrame(ctx, rects[1], 'TIME LOST vs IDEAL', `+${dMax.toFixed(1)}s`, '0');
  annotate(ctx, rects[1], model, frames, false);
  plotSeries(ctx, rects[1], model.delta, 0, dMax, 'rgba(224,59,47,0.28)', 1, true);
  plotSeries(ctx, rects[1], model.delta, 0, dMax, '#e03b2f', 1.8);

  // Marks the single frame furthest behind the ideal — the point the
  // telemetry summary's "Worst moment" stat jumps to on click.
  if (model.worstFrame >= 0 && model.worstFrame < frames) {
    const wx = rects[1].x + (model.worstFrame / (frames - 1)) * rects[1].w;
    const wy = rects[1].y + rects[1].h - (clamp(model.delta[model.worstFrame], 0, dMax) / dMax) * rects[1].h;
    ctx.fillStyle = '#ff8a3d';
    ctx.beginPath();
    ctx.arc(wx, wy, 3, 0, Math.PI * 2);
    ctx.fill();
  }

  // --- inputs -------------------------------------------------------------
  chartFrame(ctx, rects[2], 'BRAKE / STEER', '+1', '−1');
  annotate(ctx, rects[2], model, frames, false);
  ctx.strokeStyle = 'rgba(255,255,255,0.12)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(rects[2].x, rects[2].y + rects[2].h / 2);
  ctx.lineTo(rects[2].x + rects[2].w, rects[2].y + rects[2].h / 2);
  ctx.stroke();
  plotSeries(ctx, rects[2], g.steer, -1, 1, 'rgba(111,183,255,0.9)', 1.4);
  plotSeries(ctx, rects[2], g.brake, -1, 1, '#ff5a4d', 1.8);

  // --- grip usage ---------------------------------------------------------
  // The single most game-specific channel there is: this game is a friction
  // ellipse, and these two lines are how close each axle came to filling it.
  chartFrame(ctx, rects[3], 'GRIP USED — front / rear', '100%', '0');
  annotate(ctx, rects[3], model, frames, false);
  plotSeries(ctx, rects[3], g.frontLoad, 0, 1, 'rgba(232,193,74,0.9)', 1.5);
  plotSeries(ctx, rects[3], g.rearLoad, 0, 1, 'rgba(176,107,255,0.9)', 1.5);

  // --- playhead across every chart ---------------------------------------
  const px = (clamp(frame, 0, frames - 1) / (frames - 1)) * cssW;
  ctx.strokeStyle = 'rgba(255,255,255,0.85)';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(px, 0);
  ctx.lineTo(px, cssH);
  ctx.stroke();
}

/**
 * The friction circle — actually an egg, because this kart's envelope is not
 * a circle.
 *
 * Every point is one moment of the lap plotted as lateral against longitudinal
 * g. Lateral grip comes from the tyres and is roughly symmetric left and
 * right, so that axis is a true radius. Longitudinal is not symmetric: the
 * rear-brakes-only pedal (`MAX_BRAKE_FORCE`) tops out well short of tyre grip,
 * while power (`MAX_TRACTION_FORCE`) reaches further but still short of it.
 * Drawing all three as one ring at `mu` — as this used to — told a driver they
 * were leaving two-thirds of the brake circle unused when they were already on
 * the pedal stop. The compressed top is the actual shape of a rear-brake
 * envelope; it is not a rendering choice.
 */
export function drawFrictionCircle(
  canvas: HTMLCanvasElement,
  model: TelemetryModel,
  frame: number,
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const size = canvas.clientWidth || canvas.width;
  if (canvas.width !== size * dpr) {
    canvas.width = size * dpr;
    canvas.height = size * dpr;
  }
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, size, size);

  const cx = size / 2;
  const cy = size / 2;

  // Three independent limits, all in g. Lateral is the tyres' own peak, the
  // same figure the speed-profile solver corners against. Brake and power are
  // the actuator ceilings from vehicle.ts — what the pedal and the drivetrain
  // can actually ask of the rear axle, not what the rubber alone could take.
  const lateralG = SURFACES.track.mu;
  const brakeG = MAX_BRAKE_FORCE / (MASS * G);
  const powerG = MAX_TRACTION_FORCE / (MASS * G);

  const radius = size / 2 - 16;
  const pxPerG = radius / lateralG;
  const toPxX = (v: number): number => v * pxPerG;
  const toPxY = (v: number): number => v * pxPerG;
  const brakeRadius = brakeG * pxPerG;
  const powerRadius = powerG * pxPerG;

  /**
   * The envelope's own boundary in g, as a function of true screen angle.
   *
   * Two half-ellipses stitched at y = 0: semi-axes (lateralG, brakeG) above
   * the line, (lateralG, powerG) below it. Both halves pass through
   * (±lateralG, 0), so the seam is continuous.
   */
  const envelopeRadius = (angle: number): number => {
    // Canvas angle: 0 = +x (right), increasing clockwise, so sin > 0 is
    // downward — power, per the longG sign convention below.
    const ry = Math.sin(angle) < 0 ? brakeG : powerG;
    return (lateralG * ry) / Math.hypot(ry * Math.cos(angle), lateralG * Math.sin(angle));
  };

  const drawEnvelope = (scale: number, style: string, width: number): void => {
    ctx.beginPath();
    const steps = 96;
    for (let i = 0; i <= steps; i++) {
      const angle = (i / steps) * Math.PI * 2;
      const r = envelopeRadius(angle) * scale * pxPerG;
      const x = cx + Math.cos(angle) * r;
      const y = cy + Math.sin(angle) * r;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.strokeStyle = style;
    ctx.lineWidth = width;
    ctx.stroke();
  };

  drawEnvelope(1, 'rgba(255,255,255,0.22)', 1.5);
  drawEnvelope(0.5, 'rgba(255,255,255,0.08)', 1);

  ctx.strokeStyle = 'rgba(255,255,255,0.08)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(cx - radius, cy);
  ctx.lineTo(cx + radius, cy);
  ctx.moveTo(cx, cy - brakeRadius);
  ctx.lineTo(cx, cy + powerRadius);
  ctx.stroke();

  const g = model.ghost;
  for (let i = 0; i < g.latG.length; i++) {
    const x = cx + toPxX(g.latG[i]);
    // Braking is negative long-g and belongs at the top, the usual convention.
    const y = cy + toPxY(g.longG[i]);
    const ceiling = g.longG[i] < 0 ? brakeG : powerG;
    const near = Math.hypot(g.latG[i] / lateralG, g.longG[i] / ceiling);
    ctx.fillStyle = `rgba(255,138,61,${0.1 + Math.min(1, near) * 0.5})`;
    ctx.beginPath();
    ctx.arc(x, y, 1.6, 0, Math.PI * 2);
    ctx.fill();
  }

  const f = clamp(Math.round(frame), 0, g.latG.length - 1);
  ctx.fillStyle = '#f2f4f8';
  ctx.strokeStyle = '#0d1014';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(cx + toPxX(g.latG[f] ?? 0), cy + toPxY(g.longG[f] ?? 0), 4.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = '#4d5663';
  ctx.font = `700 9px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.fillText(`BRAKE  ${brakeG.toFixed(2)}g`, cx, cy - brakeRadius - 11);
  ctx.fillText(`POWER  ${powerG.toFixed(2)}g`, cx, cy + powerRadius + 11);
  ctx.textAlign = 'right';
  ctx.fillText('L', cx - radius - 4, cy + 3);
  ctx.textAlign = 'left';
  ctx.fillText('R', cx + radius + 4, cy + 3);
  ctx.fillStyle = '#6f7b8c';
  ctx.textAlign = 'center';
  ctx.fillText(`${lateralG.toFixed(2)}g lateral`, cx, cy + radius - 4);
  ctx.textAlign = 'left';
}
